// components/ChatInput.jsx - 最终优化版本
import {
  Box,
  Paper,
  Stack,
  Button,
  Typography,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Send, PhotoCamera, Close } from '@mui/icons-material';
import { useRef, useState } from 'react';
import ImageDisplay from './ImageDisplay';
import TextareaAutosize from '@mui/material/TextareaAutosize';

const ChatInput = ({
  isConnected,
  input,
  setInput,
  images,
  setImages,
  replyTo,
  setReplyTo,
  uploading,
  onImageUpload,
  onSendMessage,
  isMobile = false
}) => {
  const fileInputRef = useRef(null);

  // ✅ 本地紀錄訊息時間
  const [messageTimestamps, setMessageTimestamps] = useState([]);

  // ✅ 包一層頻率限制的發送函式
  const handleSendMessage = () => {
    const now = Date.now();
    const oneMinuteAgo = now - 60 * 1000;

    // 保留最近 1 分鐘內的紀錄
    const recent = messageTimestamps.filter((t) => t > oneMinuteAgo);

    if (recent.length >= 3) {
      alert('發送過於頻繁，每分鐘最多 3 條訊息！');
      return;
    }

    // 真的發送
    onSendMessage();
    setMessageTimestamps([...recent, now]);
  };

  // ✅ 上傳檔案檢查
  const handleFileUpload = (files) => {
    const validFiles = Array.from(files).filter((file) =>
      file.type.startsWith('image/')
    );

    if (validFiles.length !== files.length) {
      alert('只能上傳圖片檔案！');
    }

    if (validFiles.length > 0) {
      onImageUpload(validFiles);
    }

    // 清空 <input>，避免同一張圖無法重複上傳
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 根据设备类型选择占位符文本
  const getPlaceholderText = () => {
    if (isMobile) {
      return "消息";
    }
    return "輸入消息內容... (支援 @用戶名 提及 • Ctrl+Enter 快速發送)";
  };

  // 手機端使用的圖片上傳按鈕 (IconButton)
  const MobileImageUploadButton = () => (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
        accept="image/*"
        onChange={(e) => handleFileUpload(e.target.files)}
      />
      <IconButton
        size="medium"
        onClick={() => fileInputRef.current?.click()}
        disabled={!isConnected || images.length >= 3 || uploading}
        sx={{
          color: isConnected ? 'white' : 'inherit',
          '&.Mui-disabled': {
            color: 'rgba(0,0,0,0.26)',
          },
        }}
      >
        {uploading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <PhotoCamera />
        )}
      </IconButton>
    </>
  );

  // 手機端使用的發送按鈕 (IconButton)
  const MobileSendMessageButton = () => (
    <IconButton
      onClick={handleSendMessage}
      disabled={(!input.trim() && images.length === 0) || !isConnected}
      size="medium"
      sx={{ 
        color: 'white',
        backgroundColor: (theme) => theme.palette.primary.main,
        '&:hover': {
          backgroundColor: (theme) => theme.palette.primary.dark,
        },
        '&.Mui-disabled': {
          backgroundColor: 'rgba(0, 0, 0, 0.12)',
          color: 'rgba(0, 0, 0, 0.26)',
        },
      }}
    >
      <Send />
    </IconButton>
  );

  return (
    <>
      {/* 回覆預覽 (無變化) */}
      {replyTo && (
        <Paper
          sx={{
            mt: 1,
            p: isMobile ? 1.5 : 2,
            backgroundColor: isConnected ? 'rgba(76,175,80,0.2)' : 'rgba(25,118,210,0.1)',
            borderLeft: `4px solid ${isConnected ? '#4caf50' : '#1976d2'}`,
            borderRadius: 1,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  display: 'block',
                  fontSize: isMobile ? '0.7rem' : '0.75rem'
                }}
              >
                回覆 @{replyTo.username}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 0.5, 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis',
                  fontSize: isMobile ? '0.8rem' : '0.875rem'
                }}
              >
                {replyTo.message_text.substring(0, isMobile ? 50 : 100)}
                {replyTo.message_text.length > (isMobile ? 50 : 100) ? '...' : ''}
              </Typography>
            </Box>
            <IconButton 
              size="small" 
              onClick={() => setReplyTo(null)} 
              sx={{ ml: 1 }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        </Paper>
      )}

      {/* 圖片預覽 (無變化) */}
      {images.length > 0 && (
        <Paper
          sx={{
            mt: 1,
            p: isMobile ? 1 : 1.5,
            backgroundColor: isConnected ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
            borderRadius: 1,
          }}
        >
          <Typography 
            variant="caption" 
            color="text.secondary" 
            gutterBottom
            sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
          >
            待發送圖片 ({images.length}/3)
          </Typography>
          <Stack 
            direction="row" 
            spacing={1} 
            sx={{ 
              mt: 1, 
              flexWrap: 'wrap',
              gap: isMobile ? 0.5 : 1
            }}
          >
            {images.map((imageUrl, index) => (
              <Box key={index} sx={{ position: 'relative' }}>
                <ImageDisplay
                  imageUrl={imageUrl}
                  alt={`預覽${index + 1}`}
                  maxWidth={isMobile ? 60 : 80}
                  maxHeight={isMobile ? 60 : 80}
                  sx={{
                    border: '2px solid rgba(25,118,210,0.3)',
                  }}
                />
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: 'error.main',
                    color: 'white',
                    width: isMobile ? 18 : 20,
                    height: isMobile ? 18 : 20,
                    '&:hover': { backgroundColor: 'error.dark' }
                  }}
                  onClick={() => setImages(prev => prev.filter((_, i) => i !== index))}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* 輸入框容器 */}
      <Paper
        sx={{
          mt: 1,
          p: isMobile ? 1 : 2,
          backgroundColor: isConnected ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.95)',
          border: `1px solid ${isConnected ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
          borderRadius: 2,
          opacity: !isConnected ? 0.7 : 1,
        }}
      >
        <Stack 
          direction={isMobile ? "row" : "column"} // 📱 手機端：橫向排列
          alignItems={isMobile ? "flex-end" : "stretch"} // 📱 手機端：對齊底部
          spacing={isMobile ? 0.5 : 2}
        >
          {/* 1. 圖片按鈕 (手機端使用 IconButton) */}
          {isMobile && <MobileImageUploadButton />}

          {/* 2. 消息輸入框 (佔滿剩餘空間，使用定位顯示字數) */}
          <Box sx={{ 
              flexGrow: 1,
              position: 'relative' // ⚡ 關鍵改變：設定相對定位，以便子元素絕對定位
            }}>
            <TextareaAutosize
              minRows={isMobile ? 1 : 2}
              maxRows={isMobile ? 6 : 10}
              placeholder={getPlaceholderText()}
              value={input}
              onChange={(e) => setInput(e.target.value.slice(0, 300))}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={!isConnected}
              style={{
                width: '100%',
                resize: isMobile ? 'none' : 'vertical', // 手機端禁用垂直拖拉
                padding: isMobile ? '8px 35px 8px 10px' : '8px 12px', // ⚡ 關鍵改變：手機端右側留出空間給字數提示
                borderRadius: 8,
                border: isConnected ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(0,0,0,0.23)',
                backgroundColor: isConnected ? 'rgba(255,255,255,0.1)' : 'white',
                color: isConnected ? 'white' : 'inherit',
                fontSize: isMobile ? '0.9rem' : '0.95rem',
                fontFamily: 'inherit',
                outline: 'none',
                lineHeight: isMobile ? '1.2' : 'inherit',
                minHeight: isMobile ? '40px' : 'auto',
                display: 'block',
                boxSizing: 'border-box'
              }}
            />
            
            {/* ⚡ 關鍵改變：手機端字數提示 (絕對定位) */}
            {isMobile && (
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 8,
                  fontSize: '0.6rem',
                  color: isConnected ? 'rgba(255,255,255,0.6)' : 'text.secondary',
                  pointerEvents: 'none', // 確保不影響 Textarea 的點擊
                }}
              >
                {input.length}/300
              </Typography>
            )}

          </Box>

          {/* 3. 發送按鈕 (手機端使用 IconButton) */}
          {isMobile && <MobileSendMessageButton />}

          {/* 🖥️ 桌面端：按鈕和狀態在下方 (保持原樣) */}
          {!isMobile && (
            <Stack 
              direction="row" 
              justifyContent="space-between" 
              alignItems="center"
              spacing={1}
            >
              <Stack 
                direction="row" 
                spacing={1} 
                alignItems="center"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  multiple
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files)}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={uploading ? <CircularProgress size={16} /> : <PhotoCamera />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!isConnected || images.length >= 3 || uploading}
                  sx={{
                    color: isConnected ? 'white' : 'inherit',
                    borderColor: isConnected ? 'rgba(255,255,255,0.3)' : 'inherit',
                    fontSize: '0.875rem',
                    '&.Mui-disabled': {
                      color: 'rgba(0,0,0,0.26)',
                      borderColor: 'rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  {uploading ? '上傳中' : '圖片'}
                </Button>
                
                <Typography
                  variant="caption"
                  sx={{
                    color: isConnected ? 'rgba(255,255,255,0.6)' : 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  {input.length}/300
                </Typography>
              </Stack>
              
              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={(!input.trim() && images.length === 0) || !isConnected}
                endIcon={<Send />}
                sx={{ 
                  borderRadius: 2,
                  fontSize: '0.875rem',
                }}
              >
                發送
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>
    </>
  );
};

export default ChatInput;